import React from "react";
import { observer, inject } from "mobx-react";
import { computed } from "mobx";
import PersonEdit from "./PersonEdit/PersonEdit";
import PersonLogout from "./PersonLogout/PersonLogout";
import EntityDelete from "@src/Components/Common/EntityDelete/EntityDelete";
import { IManagementStore } from "@src/Stores/Management/types";
import { IValuesStore } from "@src/Stores/Values/types";
import { TableCell, Typography, Grid } from "@material-ui/core";
import PersonChangePassword from "./PersonChangePassword/PersonChangePassword";
import { STTableRow } from "@src/Components/Ui/STTableRow";
import { IAuthStore } from "@src/Stores/Auth/types";
import PersonPositionEdit from "./PersonPositionEdit/PersonPositionEdit";
import PersonPermission from "./PersonPermission/PersonPermission";
import { Values } from "@src/Tools/Values";
import { Person } from "@src/Interfaces/Entity/Person";
import { Location } from "@src/Interfaces/Entity/Location";
import { Position } from "@src/Interfaces/Entity/Position";
import _ from "lodash";
import { STTableCellTypography } from "@src/Components/Ui/STTableCellTypography";
import CreateIcon from "@material-ui/icons/Create";
import RoundButton from "@src/Shared/Components/RoundButton/RoundButton";
import Person2FA from "./Person2FA/Person2FA";

interface IProps {
  person: Person;
  values?: IValuesStore;
  auth?: IAuthStore;
  management?: IManagementStore;
  onEditClick?: (person: Person) => void;
}

@inject("values", "management", "auth")
@observer
class PersonTableItem extends React.Component<IProps> {
  @computed get isActive() {
    return this.props.person.state === this.props.values.states.Active;
  }

  @computed get canDelete() {
    return this.props.person.id !== this.props.auth.user.person.id;
  }

  @computed get canLogout() {
    const { auth, values } = this.props;
    return (
      this.props.person.id !== this.props.auth.user.person.id &&
      auth.user.roles?.includes(values.roles.Administrator) &&
      this.props.person.lastActivity &&
      this.props.person.lastActivity.hasActiveSessions
    );
  }

  @computed get positionAssigned() {
    let positionAssigned = Values.getLocalizationValue("noPositionsAssigned");
    this.props.person.positionAssignments.map(positionAssignment => {
      if (positionAssignment.locationId !== this.props.auth.user.location.id) {
        return positionAssignment;
      }
      const position = this.props.management.activePositions.find(
        position => position.id === positionAssignment.positionId
      );
      if (position) {
        positionAssigned = position.title;
      }
      return positionAssignment;
    });
    return positionAssigned;
  }

  @computed get positionName() {
    const position: Position = this.props.management.activePositions.find(
      (pos: Position) => pos.id === this.props.person.lastActivity.position
    );
    if (position) {
      return `${position.name}`.replace(" ", "\u00A0");
    }
    return "";
  }

  @computed get locationName() {
    const location: Location = this.props.management.activeLocations.find(
      (loc: Location) => loc.id === this.props.person.lastActivity.location
    );
    if (location) {
      return `${location.name}`.replace(" ", "\u00A0");
    }
    return "";
  }
  
  @computed get is2FAEnabled() {
      return this.props.person.twoFactorAuthenticationEnabled;
  }

  handleRemove = async () => {
    await this.props.management.removePerson(this.props.person.id);
  };

  handleLogout = async () => {
    await this.props.management.deactivateAllPersonSessions(this.props.person.id);
  };

  renderUserAbilities(person: Person) {
    const isAdmin: boolean = this.props.auth.user.roles.find(x => x == this.props.values.roles.Administrator)!= null;
      return (
        <Grid container wrap="nowrap" justify="flex-end">
          <>
          {isAdmin && (
            <Grid container wrap="nowrap" justify="flex-end">
                <div>
                {this.is2FAEnabled && <Person2FA person={person}/>}
                </div>
                <div>
                {this.canLogout && <PersonLogout onSubmit={this.handleLogout} />}
                </div>
                <div>
                    <PersonPermission person={person} />
                </div>
                <div>
                    <PersonChangePassword person={person} />
                </div>
                <div>
                    <PersonPositionEdit person={person} />
                </div>
                <div>
                    <RoundButton
                        icon={<CreateIcon/>}
                        tooltipTitle={Values.getLocalizationValue("edit")}
                        onClick={() => this.props.onEditClick(person)}
                    />
                </div>
                <div style={{ visibility: this.canDelete ? "visible" : "hidden"}}>
                    <EntityDelete onSubmit={this.handleRemove} />
                </div>
            </Grid>
            )}

            {!isAdmin && (
            <Grid container wrap="nowrap" justify="flex-end">
                <div>
                    <RoundButton
                        icon={<CreateIcon />}
                        tooltipTitle={Values.getLocalizationValue("edit")}
                        onClick={() => this.props.onEditClick(person)}
                    />
                </div>
            </Grid>
            )}
            </>
        </Grid>
    );
  }

    renderUserAbilitiesOnRemote(person: Person) {
        const isSysAdmin: boolean = this.props.auth.user.roles.find(x => x == this.props.values.roles.Administrator) != null;
        return (
            <Grid container wrap="nowrap" justify="flex-end">
                <div style={{ visibility: !isSysAdmin ? "visible" : "hidden" }}>
                    <PersonChangePassword person={person} />
                </div>
            </Grid>
        );
    }

  render() {
    const person: Person = this.props.person;
    var activelyAssignedPositions = _(person.positionAssignments)
      .filter(x => x.activelyAssigned)
      .map(x => {
        var location = _.find(this.props.management.activeLocations, {
          id: x.locationId,
        });
        var position = _.find(this.props.management.activePositions, {
          id: x.positionId,
        });
        return {
          locationName: _.get(location, "name"),
          positionName: _.get(position, "name"),
        };
      })
      .filter(x => !_.isEmpty(x.positionName) && !_.isEmpty(x.locationName))
      .value();
    return (
      <STTableRow hover isActive={this.isActive}>
        <TableCell component="th" scope="row" size="small">
          <Typography>
            <STTableCellTypography>{person.name}</STTableCellTypography>
          </Typography>
        </TableCell>
        <TableCell component="th" scope="row" size="small">
          <Typography>
            <STTableCellTypography>{person.fullName}</STTableCellTypography>
          </Typography>
        </TableCell>
        <TableCell component="th" scope="row" size="small">
          <Typography>
            <STTableCellTypography>{person.email}</STTableCellTypography>
          </Typography>
        </TableCell>
        <TableCell component="th" scope="row" size="small">
          {person.lastActivity ? (
            <Grid container direction="column">
              <Grid item>
                <STTableCellTypography>{this.positionName}</STTableCellTypography>
              </Grid>
              <Grid item>
                <STTableCellTypography>{person.lastActivity.timeDiff}</STTableCellTypography>
              </Grid>
            </Grid>
          ) : (
            <STTableCellTypography>—</STTableCellTypography>
          )}
          <STTableCellTypography />
        </TableCell>
        <TableCell component="th" scope="row" size="small">
          {person.lastActivity ? (
            <Grid container direction="column">
              <Grid item>
                <STTableCellTypography>{this.locationName}</STTableCellTypography>
              </Grid>
            </Grid>
          ) : (
            <STTableCellTypography>—</STTableCellTypography>
          )}
          <STTableCellTypography />
        </TableCell>
        <TableCell component="th" scope="row" size="small">
          {activelyAssignedPositions ? (
            <Grid container>
              {activelyAssignedPositions.map((assignedPosition, index) => {
                return (
                  <React.Fragment key={index}>
                    <Grid item xs={6}>
                      <STTableCellTypography>{assignedPosition.locationName}</STTableCellTypography>
                    </Grid>
                    <Grid item xs={6}>
                      <STTableCellTypography>{assignedPosition.positionName}</STTableCellTypography>
                    </Grid>
                  </React.Fragment>
                );
              })}
            </Grid>
          ) : (
            <Typography>—</Typography>
          )}
        </TableCell>
        {!this.props.auth.user.location.primary && (
          <TableCell component="th" scope="row" size="small">
            <STTableCellTypography>{this.positionAssigned}</STTableCellTypography>
          </TableCell>
        )}

        <TableCell align="right" size="small">
            {this.props.auth.user.location.primary ? this.renderUserAbilities(person) : this.renderUserAbilitiesOnRemote(person)}
        </TableCell>
      </STTableRow>
    );
  }
}

export default PersonTableItem;
